import React from 'react';
import { ArrowRight } from 'lucide-react';

// A saved mapping read like the mapping screen: one block per sheet, each file column with the
// field it feeds, coloured like the "requis / importants / optionnels" groups of that screen.

const FIELD_STYLES = {
  required: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  important: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  optional: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};
const CATEGORY_ORDER = ['required', 'important', 'optional'];
const CATEGORY_LABELS = { required: 'requis', important: 'important', optional: 'optionnel' };

const categoryOf = (choice) => (choice?.required ? 'required' : choice?.important ? 'important' : 'optional');

function SheetMapping({ sheet, mapping, isCurrent, choicesByField }) {
  // Required fields first, then important, then optional, each in the saved order
  const rows = Object.entries(mapping || {})
    .map(([column, field], index) => ({ column, field, index, category: categoryOf(choicesByField[field]) }))
    .sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) || a.index - b.index);

  return (
    <div className={`rounded-lg border p-4 ${isCurrent ? 'border-blue-400 dark:border-blue-500' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{sheet}</h4>
        {isCurrent && (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            feuille de ce chargement
          </span>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {rows.length} colonne{rows.length > 1 ? 's' : ''}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune colonne mappée.</p>
      ) : (
        <ul className="grid gap-x-8 gap-y-1.5 md:grid-cols-2">
          {rows.map(({ column, field, category }) => (
            <li key={column} className="flex min-w-0 items-center gap-2 text-sm">
              <span className="truncate text-gray-700 dark:text-gray-200" title={column}>{column}</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-xs ${FIELD_STYLES[category]}`}
                title={[choicesByField[field]?.description, `champ ${CATEGORY_LABELS[category]}`].filter(Boolean).join(' · ')}
              >
                {field}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// config is the saved mapping: [{ sheet, mapping: { file column: field } }]
function MappingConfigView({ config, currentSheet, choices = [] }) {
  if (!Array.isArray(config)) {
    // Older mappings saved in another shape: show them as they are
    return (
      <pre className="bg-gray-900 text-gray-100 text-xs p-4 rounded overflow-auto">{JSON.stringify(config, null, 2)}</pre>
    );
  }
  const choicesByField = Object.fromEntries(choices.map((choice) => [choice.value, choice]));
  // The sheet of this History entry first; the file's sheets left unmapped on one line
  const sheets = [...config]
    .filter(({ sheet, mapping }) => sheet === currentSheet || Object.keys(mapping || {}).length > 0)
    .sort((a, b) => (b.sheet === currentSheet) - (a.sheet === currentSheet));
  const unmapped = config.filter(({ sheet, mapping }) => sheet !== currentSheet && Object.keys(mapping || {}).length === 0);

  return (
    <div className="space-y-3">
      {sheets.map(({ sheet, mapping }) => (
        <SheetMapping key={sheet} sheet={sheet} mapping={mapping} isCurrent={sheet === currentSheet} choicesByField={choicesByField} />
      ))}
      {unmapped.length > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Feuille{unmapped.length > 1 ? 's' : ''} du fichier sans mapping : {unmapped.map(({ sheet }) => sheet).join(', ')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>Champs :</span>
        {CATEGORY_ORDER.map((category) => (
          <span key={category} className={`rounded px-1.5 py-0.5 ${FIELD_STYLES[category]}`}>
            {CATEGORY_LABELS[category]}
          </span>
        ))}
      </div>
    </div>
  );
}

export default MappingConfigView;
